import { MessageBarType } from '@fluentui/react';
import { DropdownOptionEx } from '../../Utilities/DropdownOptionEx';
import { Permission } from '../../Services/Permission';
import { UserContext } from '../../Utilities/Context/UserContext';
import * as DateEx from '../../Utilities/DateEx';
import { settings } from '../../context/network/http/QueryProvider/queries/UISettings';
import { CustomPropertyType, EntityType, Resource, ResourceType, UserType } from '../../api/generated/data-contracts';
import { fieldOptionsFactory, IFormSchema } from 'spark-forms';
import { systemFieldsGrouped, tpFieldOptionsFactory } from '../_builders/new/tpFieldOptionsFactory';
import { OriginRender } from '../_builders/new/components/OriginRender';
import { WorkProfileSelector } from '../_builders/new/components/WorkProfileSelector';
import { OriginIdRender } from '../_builders/new/components/OriginIdRender';
import { ResourceSpecificWorkprofileEditor } from '../../views/overviews/resource/components/ResourceSpecificWorkprofileEditor';
import { StringEx } from '../../Utilities/StringEx';
import EnumEx from '../../Utilities/EnumEx';
import { getcustomPropertiesSchema } from '../../components_new/customPropertiesSchema';
import { validateCustomProperties } from '../customProperty/helpers/customPropertyUtil';
import { originHelper } from '../_builders/new/helpers/origins/originHelper'
import { lazy } from 'react';
import { translate } from '../../Locales/i18n/localizationService';

const EntityFieldFilterEditorLazy = lazy(() => import('../_builders/new/components/EntityFieldFilterEditor'));

const skipValidation = (item: Resource) => item?.resourceType === ResourceType.WorkProfile || item?.id === '00000000-abcd-0000-9999-000000000001';

export const resourceSchema: IFormSchema<Resource> = {
    groups: {
        basic: { displayName: 'Basic', groupProps: { icon: 'EntitlementPolicy', selected: true } },
        capacity: { displayName: 'Capacity', groupProps: { icon: 'AlignVerticalBottom' } },
        permission: { displayName: 'Permission', groupProps: { icon: 'Permissions' } },
        rbs: { displayName: 'RBS', groupProps: { icon: 'BulletedTreeList' } },
        customProperties: { displayName: 'Custom properties', groupProps: { icon: 'DataManagementSettings' } },
        resourcePool: { displayName: 'Resource pool', groupProps: { icon: 'UserEvent' } },
        specificWorkProfile: { displayName: 'Specific work profile', groupProps: { icon: 'CalendarSettings' } },
        system: { displayName: 'System', groupProps: { icon: 'Settings' } },
    },
    fields: {
        active: tpFieldOptionsFactory.messageBar({
            displayName: 'Active',
            group: 'basic',
            description: translate.t("Resource.ActiveDescription"),
            config: {
                options: (item: Resource) => {
                    return {
                        message: item.active ? translate.t("Resource.ActiveState") : translate.t("Resource.InActiveState"),
                        type: item.active ? MessageBarType.success : MessageBarType.warning,
                        width: '100%',
                    };
                },
            },
            hideLabel: true,
            validation: {
                validationMark: 'required',
                validationRule: async item => (StringEx.isNullOrWhiteSpace(item.name) ? 'The resource must have a name.' : null),
            },
        }),
        name: fieldOptionsFactory.string({ displayName: translate.t("Common.Name"), group: 'basic' }),
        email: fieldOptionsFactory.string({ displayName: translate.t("Common.Email"), group: 'basic', hide: item => item.resourceType !== ResourceType.Named }), // check render
        resourceType: fieldOptionsFactory.predefinedArray({
            displayName: translate.t("Resource.ResourceType"),
            group: 'basic',
            config: { predefinedOptions: { options: DropdownOptionEx.enumToDropdownOptions<ResourceType>(ResourceType) } },
            validation: {
                validationMark: 'required',
                validationRule: async item => (item.resourceType == null ? translate.t("Resource.ResourceTypeIsRequired") : null),
            },
        }),
        relatedGenericResourceId: tpFieldOptionsFactory.resource({
            displayName: translate.t("Common.GenericResource"),
            group: 'basic',
            description: translate.t("Resource.RelatedToGenericResource"),
            disable: () => true,
            hide: item => item.resourceType !== ResourceType.Named,
            config: { nameField: 'name', descriptionField: 'rbs', resourceTypes: [ResourceType.Generic] },
        }),
        workProfileId: fieldOptionsFactory.customComponent({
            displayName: translate.t("Resource.MainWorkProfile"),
            group: 'basic',
            hide: item => item.resourceType !== ResourceType.Named && item.resourceType !== ResourceType.Generic,
            config: { component: WorkProfileSelector },
        }),
        earliestAvailable: fieldOptionsFactory.date({
            displayName: translate.t("Resource.EarliestAvailable"),
            group: 'basic',
            config: { displayFormat: (date?: Date) => DateEx.asUserLocal(date) },
            hide: item => item.resourceType !== ResourceType.Named && item.resourceType !== ResourceType.WorkProfile,
        }),
        latestAvailable: fieldOptionsFactory.date({
            displayName: translate.t("Resource.LatestAvailable"),
            group: 'basic',
            config: { displayFormat: (date?: Date) => DateEx.asUserLocal(date) },
            hide: item => item.resourceType !== ResourceType.Named && item.resourceType !== ResourceType.WorkProfile,
        }),

        manager: tpFieldOptionsFactory.resource({
            displayName: translate.t("Resource.Manager"),
            group: 'basic',
            config: { nameField: 'name', descriptionField: 'email', resourceTypes: [ResourceType.Named] },
            hide: (item: Resource) => item.resourceType !== ResourceType.Named,
        }),

        workingPercent: fieldOptionsFactory.number({
            displayName: translate.t("Resource.WorkingPercent"),
            group: 'capacity',
            description: translate.t("Resource.WorkingPercentDescription"),
            hide: (item: Resource) => item.resourceType !== ResourceType.Named || !settings().enableResourceWorkingPercentAndUtilization,
            validation: {
                validationRule: async item =>
                    item.workingPercent >= 0 && item.workingPercent <= 1 ? null : translate.t("Resource.WorkingPercentValidation"),
            },
        }),
        utilization: fieldOptionsFactory.number({
            displayName: translate.t("Resource.Utilization"),
            group: 'capacity',
            description: translate.t("Resource.UtilizationDescription"),
            hide: (item: Resource) => item.resourceType !== ResourceType.Named || !settings().enableResourceWorkingPercentAndUtilization,
            validation: {
                validationRule: async item =>
                    item.utilization >= 0 && item.utilization <= 1 ? null : translate.t("Resource.UtilizationValidation"),
            },
        }),

        userTypes: fieldOptionsFactory.predefinedArray({
            displayName: translate.t("Resource.UserTypes"),
            group: 'permission',
            hide: item => item.resourceType !== ResourceType.Named,
            disable: () => settings().enableADGroupSecurity || settings().permissions?.entraGroup?.enabled || !Permission.HasSomePermissions(UserType.SuperUser),
            config: {
                predefinedOptions: { options: DropdownOptionEx.enumToDropdownOptionsWithoutZeroOption<UserType>(UserType) },
                multiSelect: true,
            },
        }),
        userPrincipalName: fieldOptionsFactory.string({
            displayName: translate.t("Resource.UserPrincipalName"),
            group: 'permission',
            hide: item => item.resourceType !== ResourceType.Named,
            disable: item => !originHelper.resourceIsAppOriginAndUnderCreation(item, true),
        }),
        rbs: tpFieldOptionsFactory.rbs({
            displayName: translate.t("Common.Rbs"),
            group: 'rbs',
            description: translate.t("Resource.RbsDescription"),
            hide: item => item.resourceType === ResourceType.Pool || item.resourceType === ResourceType.WorkProfile,
            disable: item =>
                (item.resourceType === ResourceType.Generic && !!item.created && !settings().allowChangeRbsAndCustomPropertiesOnGenericResource) ||
                (item.id === UserContext.Resource.id && !Permission.IsSuperuser()),
            config: { mode: 'select' },
            validation: {
                validationMark: 'required',
                validationRule: async item => {
                    if (skipValidation(item)) {
                        return null;
                    }
                    return EnumEx.equalsAny(item.resourceType, ...[ResourceType.Generic, ResourceType.Named]) && StringEx.isNullOrWhiteSpace(item.rbs)
                        ? translate.t("Resource.RbsValidation")
                        : null
                }
            },
        }),
        additionalRBS: tpFieldOptionsFactory.rbs({
            displayName: translate.t("Common.AdditionalRbs"),
            group: 'rbs',
            hide: item => item.resourceType !== ResourceType.Named && item.resourceType !== ResourceType.Team,
            disable: item => (item.resourceType === ResourceType.Generic && !!item.created) || (item.id === UserContext.Resource.id && !Permission.IsSuperuser()),
            config: { mode: 'multiSelect' },
        }),
        customProperties: fieldOptionsFactory.formItem({
            displayName: translate.t("Common.CustomPropertiesCapitalized"),
            group: 'customProperties',
            description: 'Custom properties',
            config: {
                schema: {
                    schemaProvider: () => getcustomPropertiesSchema('resource', cp => cp.type === CustomPropertyType.AutoNumber),
                },
            },
            hide: (resource: Resource) =>
                resource.resourceType === ResourceType.Category ||
                resource.resourceType === ResourceType.Team ||
                (resource.resourceType === ResourceType.Pool && settings().enableDynamicResourcePools),
            validation: {
                validationMark: 'none',
                validationRule: item => {
                    if (skipValidation(item)) {
                        return null;
                    }
                    return validateCustomProperties(item, 'resource')
                }
            }
        }),
        matcher: fieldOptionsFactory.customComponent({
            displayName: translate.t("Resource.ResourcePoolMatcher"),
            group: 'resourcePool',
            description: translate.t("Resource.ResourcePoolMatcherDescription"),
            hide: item => item.resourceType !== ResourceType.Pool || !settings().enableDynamicResourcePools,
            config: {
                component: EntityFieldFilterEditorLazy,
                customProperties: () => settings().resourceCustomProperties,
                entityType: EntityType.Resource,
                schema: () => resourceSchema,
            },
        }),
        excludeFromPools: fieldOptionsFactory.boolean({
            displayName: translate.t("Resource.ExcludedFromPools"),
            group: 'resourcePool',
            hide: item => !settings().enableResourcePools || item.resourceType !== ResourceType.Named,
        }),
        enableSpecificWorkprofile: fieldOptionsFactory.boolean({
            displayName: translate.t("Resource.EnableSpecificWorkProfile"),
            group: 'specificWorkProfile',
            hide: item => item.resourceType !== ResourceType.Named,
            description: translate.t("Resource.EnableSpecificWorkProfileDescription"),
        }),
        specificWorkprofileId: fieldOptionsFactory.customComponent({
            displayName: translate.t("Resource.SpecificWorkProfile"),
            group: 'specificWorkProfile',
            hide: item => !item.enableSpecificWorkprofile || item.resourceType !== ResourceType.Named,
            config: { component: ResourceSpecificWorkprofileEditor },
        }),
        azureId: fieldOptionsFactory.string({ displayName: translate.t("Common.AzureId"), group: 'system' }),
        originIds: fieldOptionsFactory.customComponent({
            displayName: translate.t("Common.OriginIds"),
            group: 'system',
            disable: () => true,
            config: { component: OriginIdRender },
        }),
        origins: fieldOptionsFactory.customComponent({
            displayName: translate.t("Common.Origins"),
            group: 'system',
            disable: () => true,
            config: { component: OriginRender },
        }),
        teamProject: fieldOptionsFactory.string({
            displayName: translate.t("Resource.TeamProject"),
            group: 'system',
            disable: () => true,
            hide: item => item.resourceType !== ResourceType.Team,
        }),

        lastSignin: fieldOptionsFactory.date({
            displayName: translate.t("Resource.LastSignIn"),
            group: 'basic',
            config: { displayFormat: (date?: Date) => DateEx.asUserLocal(date) },
            hide: item => item.resourceType !== ResourceType.Named && item.resourceType !== ResourceType.WorkProfile,
        }),

        ...systemFieldsGrouped(),
    },
};